/**
 * SFCC order lookup frame — loading state (E20-1580, shared in E20-1658)
 *
 * The framed order-status form is cross-origin, so nothing here can read its document or tell
 * that it painted. `load` still fires on the iframe element though, and that is enough to
 * clear a loading state — which is all this does.
 *
 * The frame sits in a tall reserved box (fixed per-breakpoint heights, because a cross-origin
 * frame cannot size itself), and until it loads that box is empty. On a slow connection the
 * page reads as broken rather than loading, hence the overlay.
 *
 * The overlay, the timeout and the aria-busy bookkeeping live in util.embed-loading, shared
 * with product registration. What stays here is the one thing the two pages genuinely do
 * differently: this page owns its iframe, so it can attach `load` before navigation starts.
 *
 * ⚠️ The ordering below is load-bearing:
 *   - The overlay class is added by the shared helper, not server-rendered. If this asset is
 *     blocked or JS is off, there is no overlay to strip and the frame is simply usable.
 *   - The frame's URL arrives as `data-src` and is promoted to `src` only after the `load`
 *     listener is attached. With a real `src` in the markup the browser starts fetching during
 *     parse, and a cached or fast response fires `load` before this deferred script runs — the
 *     listener never sees it, the timeout wins, and the fallback message covers a form that
 *     loaded perfectly. A <noscript> copy in the section keeps the no-JS path.
 *
 * Part of the temporary SFCC bridge — retires with the rest of the lookup (E20-1452).
 */

import { startEmbedLoading, frameHasLoaded } from 'util.embed-loading';

// Guard wraps the whole file, not just define(): a per-section <script> is parsed once per
// section instance, and a second parse of a top-level const/class throws at parse time, before
// any guard inside could run. See docs/dev/conventions/javascript.md.
if (!customElements.get('sfcc-order-lookup')) {
  class SfccOrderLookup extends HTMLElement {
    connectedCallback() {
      this.frame = this.querySelector('iframe');
      if (!this.frame) return;

      const src = this.frame.dataset.src;
      if (!src) return;

      this.abortController = new AbortController();
      this.loading = startEmbedLoading(this);

      // Attached before the navigation starts, so the event cannot be missed.
      frameHasLoaded(this.frame, this.abortController.signal).then(() => this.loading.settle());

      this.frame.src = src;
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.loading?.destroy();
    }
  }

  customElements.define('sfcc-order-lookup', SfccOrderLookup);
}
